import { useEffect } from "react";

// Single source of truth for site-wide identity. scripts/generate-post-pages.js
// keeps its own copy of these values for the build-time <head>; change both together.
export const SITE = {
  name: "XBrutalPandaX",
  origin: "https://xbrutalpandax.com",
  description:
    "Cyber-security research by Brutal Panda — Windows internals, malware analysis, " +
    "and offensive technique write-ups with working code.",
  image: "/profile.jpg",
};

const TAGS = [
  ["name", "description", m => m.description],
  ["property", "og:site_name", () => SITE.name],
  ["property", "og:type", m => m.type],
  ["property", "og:title", m => m.ogTitle],
  ["property", "og:description", m => m.description],
  ["property", "og:url", m => m.url],
  ["property", "og:image", m => m.image],
  ["name", "twitter:card", () => "summary_large_image"],
  ["name", "twitter:title", m => m.ogTitle],
  ["name", "twitter:description", m => m.description],
  ["name", "twitter:image", m => m.image],
];

function upsert(selector, create, apply) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  apply(el);
  return el;
}

function setMeta(attr, key, content) {
  upsert(
    `meta[${attr}="${key}"]`,
    () => {
      const el = document.createElement("meta");
      el.setAttribute(attr, key);
      return el;
    },
    el => el.setAttribute("content", content)
  );
}

/**
 * Sets the document title, description, canonical URL and social tags for a route.
 * Pass `path: undefined` while data is still loading and nothing is touched, so the
 * prerendered <head> stays in place until the real values are known.
 */
export function usePageMeta({ title, description, path, image, type, noindex } = {}) {
  const docTitle = title ? `${title} | ${SITE.name}` : SITE.name;
  const ogTitle = title || SITE.name;
  const desc = description || SITE.description;
  const url = path ? `${SITE.origin}${path}` : null;
  const img = `${SITE.origin}${image || SITE.image}`;
  const ogType = type || "website";

  useEffect(() => {
    if (!url) return;

    document.title = docTitle;

    const values = { ogTitle, description: desc, url, image: img, type: ogType };
    TAGS.forEach(([attr, key, from]) => {
      const value = from(values);
      if (value) setMeta(attr, key, value);
    });

    upsert(
      'link[rel="canonical"]',
      () => {
        const el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        return el;
      },
      el => el.setAttribute("href", url)
    );

    const robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      setMeta("name", "robots", "noindex, follow");
    } else if (robots) {
      robots.remove();
    }
  }, [docTitle, ogTitle, desc, url, img, ogType, noindex]);
}
